import { getMicrocopySlots, localizeLegalLinks } from './utils';
import { escapeHtml, getItemUrl } from './shared.mjs';
import { getLanguageCode, localizeUrl } from './locale';
import { LOCALES } from './constants';
import { parseRichText, documentToPlainTextString } from './contentful';
import { FEATURED_ITEM_COUNT } from './constants';
import { initBusinessCard } from './business-card';

const borderBox = `
<svg
  width="100%"
  height="100%"
  version="1.1"
  xmlns="http://www.w3.org/2000/svg"
  aria-hidden="true"
  focusable="false"
  >
  <rect
    x="0"
    y="0"
    width="100%"
    height="100%"
    fill="none"
    stroke="var(--global__color_secondary)"
    stroke-width="var(--portfolio__stroke-width_boxes)"
    />
</svg>`;

// Domain-style headings ("giga-hamburg.de") get break opportunities after a
// hyphen and before the TLD, so the browser wraps there instead of mid-word.
const getSplitHeading = (heading) => {
  const html = escapeHtml(heading)
    .replace(/-/g, '-<wbr>')
    .replace(/\.([a-z]+)$/i, '<wbr>.$1');
  return `<h4>${html}</h4>`;
};

const getArticles = (articleConfigs) => {
  return articleConfigs.map(({ image, heading, url, text }, i) => {
    const article = document.createElement('article');
    article.className = `article-${i}`;
    const alt = image ? image.description || image.title || heading : heading;
    const src = image && image.file ? image.file.url : null;
    const imgTag = src
      ? `<img src="${src}?w=1200&q=80&fm=webp" srcset="${src}?w=600&q=80&fm=webp 600w, ${src}?w=1200&q=80&fm=webp 1200w, ${src}?w=1800&q=80&fm=webp 1800w" sizes="(max-width: 812px) 100vw, 70vw" alt="${escapeHtml(alt)}" loading="lazy" decoding="async"/>`
      : '';
    const href = getItemUrl({ url, heading });
    const caption = `<figcaption>${getSplitHeading(heading)}</figcaption>`;

    article.innerHTML = [
      '<figure>',
      href
        ? `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${caption}</a>`
        : caption,
      imgTag,
      '</figure>',
      '<div class="info-block">',
      borderBox,
      `<div class="info-text">${parseRichText(text)}</div>`,
      '</div>',
    ].join('');

    return article;
  });
};

const truncate = (str, max) => {
  if (str.length <= max) return str;
  const truncated = str.slice(0, max).replace(/\s+\S*$/, '');
  return `${truncated}…`;
};

const getOverflowBlock = (overflowItems) => {
  const container = document.createElement('div');
  container.className = 'overflow-block';

  const inner = document.createElement('div');
  inner.className = 'overflow-inner';

  const svgWrapper = document.createElement('div');
  svgWrapper.className = 'overflow-border';
  svgWrapper.insertAdjacentHTML('afterbegin', borderBox);
  inner.appendChild(svgWrapper);

  const list = document.createElement('ul');
  list.className = 'overflow-list';
  overflowItems.forEach(({ heading, url, text }) => {
    const li = document.createElement('li');
    li.className = 'overflow-item';
    const href = getItemUrl({ url, heading });
    const title = document.createElement(href ? 'a' : 'span');
    title.className = 'overflow-title';
    if (href) {
      title.href = href;
      title.target = '_blank';
      title.rel = 'noopener noreferrer';
    }
    title.textContent = heading;
    li.appendChild(title);
    if (text) {
      const desc = document.createElement('span');
      desc.className = 'overflow-desc';
      desc.textContent = truncate(documentToPlainTextString(text), 180);
      li.appendChild(desc);
    }
    list.appendChild(li);
  });
  inner.appendChild(list);

  container.appendChild(inner);
  return container;
};

const getSections = (sections) =>
  sections.map(({ heading, items }, i) => {
    const section = document.createElement('section');
    section.className = `section-${i}`;
    section.insertAdjacentHTML(
      'afterbegin',
      `<h3 class="section-heading">${escapeHtml(heading)}</h3>`
    );

    const featured = items.slice(0, FEATURED_ITEM_COUNT);
    const overflow = items.slice(FEATURED_ITEM_COUNT);

    getArticles(featured).forEach((article) => section.appendChild(article));

    if (overflow.length) {
      section.appendChild(getOverflowBlock(overflow));
    }

    return section;
  });

const getSegments = (segments) =>
  segments.reduce((acc, { heading, items }) => {
    const h2 = document.createElement('h2');
    h2.className = 'section-heading';
    h2.textContent = heading;
    return [...acc, h2, ...getSections(items)];
  }, []);

const replaceMicrocopy = (microcopy) => {
  getMicrocopySlots().forEach((slot) => {
    const value = microcopy[slot.id];
    if (!value) {
      console.error(`No microcopy found for "${slot.id}"`);
      return;
    }
    slot.innerHTML = parseRichText(value);
  });
};

const setMeta = (selector, value) => {
  const el = document.querySelector(`meta[${selector}]`);
  if (el) el.setAttribute('content', value);
};

const initHead = (microcopy, locale) => {
  const title = documentToPlainTextString(microcopy['meta.title']).trim();
  if (title) {
    document.title = title;
    setMeta('property="og:title"', title);
  }

  const description = documentToPlainTextString(
    microcopy['meta.description']
  ).trim();
  if (description) {
    setMeta('property="og:description"', description);
    setMeta('name="description"', description);
  }

  // The build wrote og:url, og:locale and og:image for this language; only a
  // template without a canonical (dev server) needs them derived here.
  if (document.querySelector('link[rel="canonical"]')) return;
  const { origin } = window.location;
  setMeta('property="og:url"', `${origin}/`);
  setMeta(
    'property="og:locale"',
    getLanguageCode(locale) === 'de' ? 'de_DE' : 'en_US'
  );
  const image = document.querySelector('meta[property="og:image"]');
  if (image) {
    image.setAttribute(
      'content',
      image.getAttribute('content').replace(/^https?:\/\/[^/]*/, origin)
    );
  }
};

const setLinkTag = (rel, attrs) => {
  const selector = Object.entries(attrs)
    .filter(([k]) => k !== 'href')
    .map(([k, v]) => `[${k}="${v}"]`)
    .join('');
  let link = document.querySelector(`link[rel="${rel}"]${selector}`);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  Object.entries(attrs).forEach(([k, v]) => link.setAttribute(k, v));
};

const initLinkTags = (locale) => {
  // The build already wrote canonical and hreflang links for this language.
  if (document.querySelector('link[rel="canonical"]')) return;
  const { origin } = window.location;
  setLinkTag('canonical', { href: `${origin}/` });
  LOCALES.forEach((other) => {
    setLinkTag('alternate', {
      hreflang: getLanguageCode(other),
      href: localizeUrl(`${origin}/`, locale, other),
    });
  });
};

const initHero = (microcopy) => {
  const tagline = document.querySelector('.landing-intro__tagline');
  const h1Tagline = document.querySelector('.landing-h1-tagline');
  const contact = document.querySelector('.landing-intro__contact');
  const subheading = documentToPlainTextString(
    microcopy['company.subheading']
  ).trim();
  if (tagline) tagline.textContent = subheading;
  if (h1Tagline) h1Tagline.textContent = subheading;
  if (contact) {
    contact.innerHTML = parseRichText(microcopy['contact.email']);
  }
};

const initSvgDimensions = () => {
  const svg = document.querySelector('svg#logo');
  const { innerHeight: height, innerWidth: width } = window;
  svg.setAttribute('width', width);
  svg.setAttribute('height', height);

  if (window.innerWidth > 812) return;
  const halfWidth = width / 2;
  const halfHeight = height / 2;

  const text = svg.querySelector('text#main');
  text.setAttribute(
    'transform',
    `scale(0.5) translate(${halfWidth}, ${halfHeight})`
  );
};

const cache = {
  logo: null,
};

// The logo SVG is mutated by the scroll animation; restore the pristine
// markup before re-initialising so animations start from a clean state.
const initSvg = () => {
  const svgWrapper = document.querySelector('.landing-logo');
  if (!cache.logo) {
    cache.logo = svgWrapper.innerHTML;
  }
  svgWrapper.innerHTML = cache.logo;
};

export default (config) => {
  const { microcopy, sections, locale } = config;

  replaceMicrocopy(microcopy);
  localizeLegalLinks(getLanguageCode(locale));
  initBusinessCard(microcopy);
  initHead(microcopy, locale);
  initLinkTags(locale);
  initHero(microcopy);
  initSvg();
  initSvgDimensions();

  const container = document.querySelector('.dynamic');
  container.innerHTML = '';
  getSegments(sections).forEach((segment) => container.appendChild(segment));

  return config;
};
